import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { useContent } from "../lib/ContentContext";

/**
 * Sticky editorial nav with the Alta Agency logo as the brand mark.
 * - variant="public": guest CTA on the right (link to /convidado)
 * - variant="admin": admin badge on the right
 * - variant="hero": transparent bg, white logo; auto-swaps to light theme once scrolled past 90vh
 * - variant="guest": logged guest area — shows name + visible logout
 */
export default function Nav({ variant = "public", guestName, onLogout, onConfirm, badgeLabel, logoutLabel, confirmLabel }) {
  const { pathname } = useLocation();
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (variant !== "hero") return;
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.85);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  const isHero = variant === "hero" && !scrolled;

  const containerClass = isHero
    ? "fixed top-0 left-0 right-0 z-50 bg-transparent transition-colors duration-500"
    : "fixed top-0 left-0 right-0 z-50 border-b border-neutral-200 bg-[#F9F8F6]/90 backdrop-blur-xl transition-colors duration-500";

  const textClass = isHero ? "text-[#F9F8F6]" : "text-neutral-900";
  const dividerClass = isHero ? "text-[#F9F8F6]/40" : "text-neutral-300";
  const softTextClass = isHero ? "text-[#F9F8F6]/80" : "text-neutral-700";

  return (
    <nav data-testid="site-nav" className={containerClass}>
      {/* Alta signature ribbon — thin red line at the very top */}
      <div className="alta-ribbon" data-testid="alta-ribbon" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/evento" data-testid="nav-logo" className="flex items-center gap-4 vlink">
          <Logo
            variant={isHero ? "light" : "dark"}
            size={44}
            testid="nav-alta-logo"
            className="opacity-95"
          />
          <span className="hidden md:inline-flex items-center gap-3">
            <span
              className={`serif text-[15px] tracking-[0.24em] uppercase ${textClass}`}
            >
              Casa <span className="text-[color:var(--alta-red)]">Alta</span>
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--alta-red)]" aria-hidden="true" />
          </span>
        </Link>

        {/* Meta (hidden for guest to make room for name) */}
        {variant !== "guest" && (
          <div
            className={`hidden md:flex items-center gap-8 mono text-[11px] uppercase ${softTextClass}`}
          >
            <span>São Paulo</span>
            <span className={dividerClass}>/</span>
            <span>Ago 2026</span>
            <span className={dividerClass}>/</span>
            <span>Private</span>
          </div>
        )}

        {variant === "guest" && (
          <div className="hidden md:flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
            <span className="rec-dot" aria-hidden="true" />
            <span>{badgeLabel || "Convite ativado"}</span>
            {guestName && (
              <>
                <span className={dividerClass}>·</span>
                <span className="text-neutral-900 tracking-[0.18em]">{guestName}</span>
              </>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="flex items-center gap-4">
          {(variant === "public" || variant === "hero") && pathname !== "/convidado" && (
            <>
              <Link
                to="/"
                data-testid="nav-cta-reality"
                className={`mono text-[11px] uppercase tracking-[0.24em] ${textClass} hover:opacity-70 transition-opacity vlink inline-flex items-center gap-2`}
              >
                <span className="rec-dot !w-2 !h-2" aria-hidden="true" />
                {content.reality?.nav_public_link || "Reality"}
              </Link>
              <Link
                to="/convidado"
                data-testid="nav-cta-guest"
                className={`mono text-[11px] uppercase tracking-[0.24em] ${textClass} hover:opacity-70 transition-opacity vlink`}
              >
                Convidado →
              </Link>
            </>
          )}
          {variant === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-cta-admin"
              className={`mono text-[11px] uppercase tracking-[0.24em] ${textClass} vlink`}
            >
              Painel
            </Link>
          )}
          {variant === "guest" && (
            <>
              {onConfirm && (
                <button
                  type="button"
                  onClick={onConfirm}
                  data-testid="nav-confirm-cta"
                  className="btn-red-primary !py-2 !px-4 !text-[10px]"
                >
                  {confirmLabel || "Confirmar vaga →"}
                </button>
              )}
              {onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  data-testid="nav-logout"
                  className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 hover:text-[color:var(--alta-red)] transition-colors border-b border-transparent hover:border-[color:var(--alta-red)] pb-0.5"
                >
                  {logoutLabel || "Sair"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
